import { PaginatedDataDTO } from '@bottega52/commons-pagination';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import { Warning } from '@material-ui/icons';
import _ from 'lodash';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import UserDemoPlantAssociationForm from '../../components/Forms/UserDemoPlantAssociationForm';
import { ModalTypes } from '../../components/Modals/ModalTypes';
import DemoPlantSearchBar from '../../components/SearchBar/DemoPlantSearchBar';
import UserDemoPlantsTable from '../../components/Users/UserDemoPlantsTable';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as DomainsActions from '../../redux/domains/domains.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as SettingsActions from '../../redux/settings/settings.actions';
import { IState } from '../../redux/store';
import * as UserActions from '../../redux/user/user.actions';
import { IDemoPlantInDTO } from '../../repository/jago/model/input/IDemoPlantInDTO';
import { IDemoPlantSearchDTO } from '../../repository/jago/model/input/IDemoPlantSearchDTO';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { IUserInDTO } from '../../repository/jago/model/input/IUserInDTO';
import { IDemoPlantUserAssociationOutDTO } from '../../repository/jago/model/output/IDemoPlantUserAssociationDTO';
import { IUserDemoPlantsRequestParamsDTO, IUserRequestParamsDTO, IWalletPlantsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { IUserOptionDTO } from '../../repository/jago/model/input/IUserOptionDTO';
import { getDomainsOptions, getDomainURLFromHostname, getUsersOptions } from '../../repository/utils';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS, Roles } from '../../services/PermissionService/PermissionConstants';
import { IDomainConfigParsedDTO } from '../../repository/jago/model/input/IDomainConfigInDTO';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  header: {
    backgroundColor: 'white',
    width: '100%',
    zIndex: 200,
    padding: 10,
    paddingRight: 20,
    borderBottom: '2px solid #5AC0B1',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 160px)',
    width: '100%'
  }
});

export interface IUsersDemoPlantsPageState {
  searchText: string;
  pageSize: number;
  isLoading: boolean;
  usersOptions: IUserOptionDTO[];
  domainsOptions: IDomainOptionDTO[];
}

type IReduxProps = ConnectedProps<typeof connector> & RouteComponentProps<any>;

export interface IUsersDemoPlantsPageProps extends WithStyles<typeof styles> {
  language: string;
  demoPlants: PaginatedDataDTO<IDemoPlantInDTO>;
}

export type ComponentProps = IUsersDemoPlantsPageProps & IReduxProps;

export class UsersDemoPlantsPage extends React.Component<ComponentProps, IUsersDemoPlantsPageState> {
  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      searchText: '',
      pageSize: 100,
      isLoading: false,
      usersOptions: [],
      domainsOptions: [],
    }
  }

  public async componentDidMount() {
    const { pageSize } = this.state;
    try {
      this.setState({ isLoading: true });
      await this.onFetchDemoPlants(0, pageSize);
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  }

  async onFilterDemoPlants(values: IDemoPlantSearchDTO) {
    const { dispatch, userData } = this.props;
    const isSofiaAdmin = AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_SOFIA_ADMIN]);
    let params: IUserDemoPlantsRequestParamsDTO = { ...values, page: 0, pageSize: 100 };
    if (!isSofiaAdmin) {
      params = { ...params, userId: userData?.id };
    }
    const filteredParams: IUserDemoPlantsRequestParamsDTO = _.pickBy(params, param => param !== '') as IUserDemoPlantsRequestParamsDTO;
    await dispatch<any>(CreditsActions.fetchDemoPlants(filteredParams));
  }

  async onFetchDemoPlants(page: number = 0, pageSize: number = 100) {
    const { dispatch, userData } = this.props;
    const isSofiaAdmin = AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_SOFIA_ADMIN]);
    let params: IUserDemoPlantsRequestParamsDTO = { page, pageSize };
    if (!isSofiaAdmin) {
      params = { ...params, userId: userData?.id };
    }
    try {
      this.setState({ isLoading: true });
      await dispatch<any>(CreditsActions.fetchDemoPlants(params));
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  }

  public handleChangeRowsPerPage(pageSize) {
    this.setState({ pageSize }, () => {
      this.onFetchDemoPlants(0, pageSize)
    });
  }

  public async onFetchDemoPlantsOnPage(page: number) {
    const { pageSize } = this.state;
    this.onFetchDemoPlants(page, pageSize);
  }

  public async onOpenDemoPlant(demoPlant: IDemoPlantInDTO) {
    const hostname = demoPlant.plant.hostname ? getDomainURLFromHostname(demoPlant.plant.hostname) : '-';
    window.open(hostname, '_blank');
  }

  public onNewDemoPlant() {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_NEW_USER_DOMAIN_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <UserDemoPlantAssociationForm
            onSubmit={(values: IDemoPlantUserAssociationOutDTO) => this.onCreateNewDemoPlantAssociation(values)}
          />
        ),
        titleMessageKey: 'demoSystem.newDemoPlantAssociation',
      }
    }));
  }

  async onOpenDemoPlantAssociationDetails(demoPlant: IDemoPlantInDTO) {
    const { dispatch } = this.props;
    let domainConfig: IDomainConfigParsedDTO = {} as IDomainConfigParsedDTO;
    try {
      domainConfig = await dispatch<any>(DomainsActions.fetchSofiaDomainConfigurationByHostname(demoPlant.plant.hostname));
    } catch (error) {
      console.log(error);
    }
    dispatch<any>(ModalsActions.showModal(`DEMO_PLANT_ASSOCIATION_DETAILS_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <UserDemoPlantAssociationForm
            demoPlantConfig={domainConfig}
            demoPlantAssociation={demoPlant}
            onSubmit={(values: IDemoPlantUserAssociationOutDTO) => this.onEditDemoPlantAssociation(demoPlant.id, values)}
          />
        )
      }
    }));
  }

  public async onCreateNewDemoPlantAssociation(values: IDemoPlantUserAssociationOutDTO) {
    const { dispatch } = this.props;
    try {
      await dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(CreditsActions.createUserDemoPlantAssociation(values));
      this.onSaveDemoPlantConfiguration(values.domainSelected.value.hostname, values.domainConfig);
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      await this.onFetchDemoPlants(0, this.state.pageSize);
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_NEW_USER_DOMAIN_MODAL`));
      dispatch<any>(ModalsActions.showModal(`SUCCESS_ALERT`, {
        modalType: ModalTypes.SUCCESS_ALERT,
        modalProps: {
          titleMessageKey: 'demoSystem.demoPlantAssociationCompleted',
          }
        }));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DEMO_PLANT_ASSOCIATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'demoSystem.createDemoPlantAssociationError',
        }
      }));
    }
  }

  public async onEditDemoPlantAssociation(associationId: number, values: IDemoPlantUserAssociationOutDTO) {
    const { dispatch } = this.props;
    try {
      await dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(CreditsActions.editUserDemoPlantAssociation(associationId, values));
      await this.onSaveDemoPlantConfiguration(values.domainSelected.value.hostname, values.domainConfig);
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      await this.onFetchDemoPlants(0, this.state.pageSize);
      dispatch<any>(ModalsActions.hideModal(`DEMO_PLANT_ASSOCIATION_DETAILS_MODAL`));
      dispatch<any>(ModalsActions.showModal(`SUCCESS_ALERT`, {
        modalType: ModalTypes.SUCCESS_ALERT,
        modalProps: {
          titleMessageKey: 'demoSystem.demoPlantAssociationEditedCompleted',
          }
        }));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DEMO_PLANT_ASSOCIATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'demoSystem.editDemoPlantAssociationError',
        }
      }));
    }
  }

  async onSaveDemoPlantConfiguration(hostname: string, domainConfig: IDomainConfigParsedDTO) {
    const { dispatch } = this.props;
    await dispatch<any>(DomainsActions.updateSofiaDomainConfiguration(hostname, { data: JSON.stringify(domainConfig) }));
  }


  onDeleteDemoPlantAssociation(demoPlant: IDemoPlantInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`DELETE_DEMO_PLANT_ASSOCIATION_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <Warning style={{ color: 'red', fontSize: 50 }} />,
        titleMessageKey: 'forms.warning',
        successMessageKey: 'demoSystem.deleteDemoPlantAssociation',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onDeleteDemoPlantAssociationConfirm(demoPlant),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`DELETE_DEMO_PLANT_ASSOCIATION_CONFIRM_MODAL`)),
      }
    }));
  }

  async onDeleteDemoPlantAssociationConfirm(demoPlant: IDemoPlantInDTO) {
    const { dispatch } = this.props;
    try { 
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(CreditsActions.deleteUserDemoPlantAssociation(demoPlant.id));
      dispatch<any>(ModalsActions.hideModal(`DELETE_DEMO_PLANT_ASSOCIATION_CONFIRM_MODAL`));
      await this.onFetchDemoPlants(0, this.state.pageSize);
    dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DEMO_PLANT_ASSOCIATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'demoSystem.deleteDemoPlantAssociationError',
        }
      }));
    }
  }

  onExportCSV() {
    const { dispatch } = this.props;
    dispatch<any>(CreditsActions.exportDemoPlantsCSV());
  }

  onResetSearch() {
    const { dispatch } = this.props;
    const { pageSize } = this.state;
    dispatch<any>(CreditsActions.fetchDemoPlants({ page: 0, pageSize }));
  }

  async onSearchDomains(value: string) {
    const { dispatch } = this.props as any;
    const params: IWalletPlantsRequestParamsDTO = { page: 0, pageSize: 100, hostname: value, walletId: undefined, isCloudCredits: false };
    const domains = await dispatch<any>(CreditsActions.fetchDomainsSearch(params));
    const domainsOptions = getDomainsOptions(domains);
    this.setState({ domainsOptions: domainsOptions as IDomainOptionDTO[] });
  }

  async onSearchUsers(value: string) {
    const { dispatch } = this.props as any;
    const params: IUserRequestParamsDTO = { search: value, page: 0, pageSize: 1000 };
    const filteredParams: IUserRequestParamsDTO = _.pickBy(params, param => param !== '') as IUserRequestParamsDTO;
    const users: PaginatedDataDTO<IUserInDTO>= await dispatch<any>(UserActions.fetchUsers(filteredParams));
    const usersOptions = getUsersOptions(users.content);
    this.setState({ usersOptions: usersOptions as IUserOptionDTO[] });
  }

  render() {
    const { classes, demoPlants } = this.props;
    const { pageSize, isLoading, usersOptions, domainsOptions } = this.state;

    const isSofiaAdmin = AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_SOFIA_ADMIN]);
    const canCreateDemoPlant = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.DOMAIN_DEMO_CREATE);

    return (
      <div className={classes.container}>
        <div className={classes.header}>
          <DemoPlantSearchBar
            onSearch={(values: IDemoPlantSearchDTO) => this.onFilterDemoPlants(values)}
            onResetSearch={() => this.onResetSearch()}
            onCreateNewEntityButtonClick={canCreateDemoPlant ? () => this.onNewDemoPlant() : undefined}
            onFilterUsers={(value: string) => this.onSearchUsers(value)}
            onFilterDomains={(value: string) => this.onSearchDomains(value)}
            onExportCSV={() => this.onExportCSV()}
            usersOptions={usersOptions}
            plantsOptions={domainsOptions}
          />
        </div>
        <div className={classes.tableContainer}>
          <UserDemoPlantsTable
            demoPlants={demoPlants}
            rowsPerPage={pageSize}
            isLoading={isLoading}
            onPageSizeChange={(pageSize) => this.handleChangeRowsPerPage(pageSize)}
            onFetchElementsOnPage={page => this.onFetchDemoPlantsOnPage(page)}
            onOpenDomain={demoPlant => this.onOpenDemoPlant(demoPlant)}
            onDeleteDomain={demoPlant => this.onDeleteDemoPlantAssociation(demoPlant)}
            onRowClick={isSofiaAdmin ? demoPlant => this.onOpenDemoPlantAssociationDetails(demoPlant) : undefined}
          />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    demoPlants: state.credits.demoPlants?.data,
    userData: state.user.userData,
  };
}

const connector = connect(mapStateToProps);

export default connector(withRouter(withStyles(styles)(UsersDemoPlantsPage))); 